import { H1, H2, H3, H4, H5, H6, P } from "@expo/html-elements";
import React, { useMemo, useRef } from "react";
import { StyleSheet, Text } from "react-native";
import { useScreenSizes } from "../../hooks/useSizes";
import useStyles from "../../hooks/useStyles";
import useTheme from "../../hooks/useTheme";
import { fontWeightFromName, getFontSize } from "../../utils/dimensions";
import { weightToFont } from "../../utils/helpers";
import { makeConditionalStyles } from "../../utils/styles";
const LINE_HEIGHT_MULTIPLIER = 1.5;
const createStyles = _ref => {
  let {
    theme,
    stylesProps
  } = _ref;
  const {
    fontWeight: weight,
    fSize,
    fHeight,
    transform,
    fontStyle,
    color,
    role,
    align,
    isDesktop,
    decoration
  } = stylesProps;
  const fColor = color || theme.fonts.roles[role].colors.default;
  const styles = StyleSheet.create({
    text: {
      marginHorizontal: 0,
      marginVertical: 0,
      paddingHorizontal: 0,
      paddingVertical: 0,
      fontFamily: weightToFont(weight),
      fontSize: fSize,
      fontWeight: fontWeightFromName(weight),
      lineHeight: fHeight,
      textTransform: transform,
      fontStyle,
      color: fColor,
      textAlign: align,
      ...makeConditionalStyles({
        textDecorationLine: decoration,
        textDecorationColor: fColor
      }, !isDesktop)
    }
  });
  return styles;
};
const Typography = _ref2 => {
  let {
    children,
    variant = "p",
    component,
    style,
    size = "default",
    fontWeight = "regular",
    onPress,
    decoration = "none",
    transform = "none",
    fontStyle = "normal",
    role = "primary",
    color,
    lineHeight,
    numberOfLines,
    align = "left"
  } = _ref2;
  const screenSizes = useScreenSizes();
  const theme = useTheme();
  const ref = useRef(null);
  const {
    fSize,
    fHeight
  } = useMemo(() => {
    let curSize = theme.fonts.semantics.span.sizes.default;
    let curHeight = theme.fonts.semantics.span.heights.default;
    switch (variant) {
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6":
        curSize = getFontSize({
          sizes: screenSizes,
          dimensions: theme.fonts.semantics[variant].sizes
        });
        curHeight = getFontSize({
          sizes: screenSizes,
          dimensions: theme.fonts.semantics[variant].heights
        });
        break;
      case "span":
        curSize = theme.fonts.semantics.span.sizes[size];
        curHeight = theme.fonts.semantics.span.heights[size];
        break;
      case "p":
      default:
        if (size === "large") {
          curSize = getFontSize({
            sizes: screenSizes,
            dimensions: theme.fonts.semantics.p.sizes.large
          });
          curHeight = getFontSize({
            sizes: screenSizes,
            dimensions: theme.fonts.semantics.p.heights.large
          });
        } else {
          curSize = theme.fonts.semantics.p.sizes[size];
          curHeight = theme.fonts.semantics.p.heights[size];
        }
    }
    if (typeof size === "number" && size) {
      curSize = size;
      curHeight = Math.round(size * LINE_HEIGHT_MULTIPLIER);
    }
    if (typeof lineHeight === "number" && lineHeight) {
      curHeight = lineHeight;
    }
    return {
      fSize: curSize,
      fHeight: curHeight
    };
  }, [theme, size, screenSizes, variant, lineHeight]);
  const alias = component || variant;
  const Component = useMemo(() => {
    switch (alias) {
      case "h1":
        return H1;
      case "h2":
        return H2;
      case "h3":
        return H3;
      case "h4":
        return H4;
      case "h5":
        return H5;
      case "h6":
        return H6;
      case "span":
        return Text;
      case "p":
      default:
        return P;
    }
  }, [alias]);
  const stylesProps = useMemo(() => {
    return {
      fontWeight,
      fSize,
      fHeight,
      transform,
      fontStyle,
      color,
      role,
      align,
      isDesktop: screenSizes.desktop,
      decoration
    };
  }, [fontWeight, fSize, fHeight, transform, fontStyle, color, role, align, screenSizes.desktop, decoration]);
  const styles = useStyles(createStyles, stylesProps);
  const textStyles = useMemo(() => {
    return [styles.text, style];
  }, [styles.text, style]);
  return /*#__PURE__*/React.createElement(Component, {
    ref: ref,
    style: textStyles,
    onPress: onPress,
    numberOfLines: numberOfLines
  }, children);
};
export { Typography };